import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiGlobe, FiPlay } from 'react-icons/fi';
import Portal from '../Portal';

const EndpointManagement = () => {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddEndpoint, setShowAddEndpoint] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [testingEndpoint, setTestingEndpoint] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadEndpoints();
  }, []);

  const loadEndpoints = async () => {
    try {
      const response = await adminAPI.getEndpoints();
      setEndpoints(response.data.endpoints);
    } catch (error) {
      showMessage('Failed to load endpoints', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleDeleteEndpoint = async (endpointId) => {
    if (!window.confirm('Are you sure you want to delete this endpoint? This action cannot be undone.')) {
      return;
    }

    try {
      await adminAPI.deleteEndpoint(endpointId);
      setEndpoints(endpoints.filter(endpoint => endpoint.id !== endpointId));
      showMessage('Endpoint deleted successfully', 'success');
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to delete endpoint', 'error');
    }
  };

  const handleTestEndpoint = async (endpoint) => {
    setTestingEndpoint(endpoint.id);
    try {
      const response = await adminAPI.testEndpoint(endpoint);
      showMessage(`Endpoint test successful! Response: ${JSON.stringify(response.data).substring(0, 100)}...`, 'success');
    } catch (error) {
      showMessage(error.response?.data?.error || 'Endpoint test failed', 'error');
    } finally {
      setTestingEndpoint(null);
    }
  };

  const filteredEndpoints = endpoints.filter(endpoint =>
    endpoint.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    endpoint.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
    endpoint.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading endpoints...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h1 style={{ margin: 0, fontSize: '24px' }}>API Endpoint Management</h1>
          <button
            onClick={() => setShowAddEndpoint(true)}
            className="btn btn-primary"
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              padding: '8px 16px',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            <FiPlus style={{ marginRight: '5px' }} />
            Add Endpoint
          </button>
        </div>
      </div>

      {message && (
        <div 
          className={messageType === 'error' ? 'error-message' : 'success-message'}
          style={{ marginBottom: '20px' }}
        >
          {message}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">API Endpoints ({filteredEndpoints.length})</h2>
            <div style={{ position: 'relative', width: '300px' }}>
              <FiSearch style={{ 
                position: 'absolute', 
                left: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#666'
              }} />
              <input
                type="text"
                placeholder="Search endpoints..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ paddingLeft: '35px' }}
              />
            </div>
          </div>
        </div>

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ minWidth: '1200px' }}>
            <thead>
              <tr>
                <th style={{ width: '180px', minWidth: '180px' }}>Name</th>
                <th style={{ width: '80px', minWidth: '80px' }}>Method</th>
                <th style={{ width: '350px', minWidth: '350px' }}>URL</th>
                <th style={{ width: '200px', minWidth: '200px' }}>Description</th>
                <th style={{ width: '100px', minWidth: '100px' }}>Auth Required</th>
                <th style={{ width: '80px', minWidth: '80px' }}>Status</th>
                <th style={{ width: '140px', minWidth: '140px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEndpoints.map(endpoint => (
                <tr key={endpoint.id}>
                  <td style={{ width: '180px', minWidth: '180px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FiGlobe style={{ marginRight: '8px', color: '#007bff', flexShrink: 0 }} />
                      <strong style={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {endpoint.name}
                      </strong>
                    </div>
                  </td>
                  <td style={{ width: '80px', minWidth: '80px' }}>
                    <span className={`badge ${getMethodBadgeClass(endpoint.method)}`}>
                      {endpoint.method}
                    </span>
                  </td>
                  <td style={{ width: '350px', minWidth: '350px' }}>
                    <div style={{ 
                      maxWidth: '350px',
                      overflow: 'hidden'
                    }}>
                      <code style={{ 
                        backgroundColor: '--bg-primary', 
                        padding: '4px 8px', 
                        borderRadius: '4px',
                        fontSize: '11px',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                      title={endpoint.url}
                      >
                        {endpoint.url}
                      </code>
                    </div>
                  </td>
                  <td style={{ width: '200px', minWidth: '200px' }}>
                    <div style={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '200px'
                    }}
                    title={endpoint.description || 'No description'}
                    >
                      {endpoint.description || 'No description'}
                    </div>
                  </td>
                  <td style={{ width: '100px', minWidth: '100px' }}>
                    <span 
                      className={`badge ${endpoint.requiresAuth ? 'badge-warning' : 'badge-success'}`}
                    >
                      {endpoint.requiresAuth ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={{ width: '80px', minWidth: '80px' }}>
                    <span 
                      className={`badge ${endpoint.isActive ? 'badge-success' : 'badge-secondary'}`}
                    >
                      {endpoint.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ width: '140px', minWidth: '140px' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-start' }}>
                      <button
                        onClick={() => handleTestEndpoint(endpoint)}
                        disabled={testingEndpoint === endpoint.id}
                        className="btn btn-small btn-info"
                        title="Test Endpoint"
                        style={{ minWidth: '32px' }}
                      >
                        {testingEndpoint === endpoint.id ? '...' : <FiPlay />}
                      </button>
                      <button
                        onClick={() => setEditingEndpoint(endpoint)}
                        className="btn btn-small btn-secondary"
                        title="Edit Endpoint"
                        style={{ minWidth: '32px' }}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => handleDeleteEndpoint(endpoint.id)}
                        className="btn btn-small btn-danger"
                        title="Delete Endpoint"
                        style={{ minWidth: '32px' }}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredEndpoints.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {searchTerm ? 'No endpoints found matching your search.' : 'No API endpoints configured.'}
              {!searchTerm && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    onClick={() => setShowAddEndpoint(true)}
                    className="btn btn-primary"
                  >
                    Add Your First API Endpoint
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showAddEndpoint && (
        <Portal>
          <EndpointModal
            onClose={() => setShowAddEndpoint(false)}
            onEndpointAdded={(newEndpoint) => {
              setEndpoints([...endpoints, newEndpoint]);
              setShowAddEndpoint(false);
              showMessage('Endpoint created successfully', 'success');
            }}
            onError={(error) => showMessage(error, 'error')}
          />
        </Portal>
      )}

      {editingEndpoint && (
        <Portal>
          <EndpointModal
            endpoint={editingEndpoint}
            onClose={() => setEditingEndpoint(null)}
            onEndpointUpdated={(updatedEndpoint) => {
              setEndpoints(endpoints.map(endpoint => 
                endpoint.id === updatedEndpoint.id ? updatedEndpoint : endpoint
              ));
              setEditingEndpoint(null);
              showMessage('Endpoint updated successfully', 'success');
            }}
            onError={(error) => showMessage(error, 'error')}
          />
        </Portal>
      )}
    </div>
  );
};

const getMethodBadgeClass = (method) => {
  switch (method.toUpperCase()) {
    case 'GET': return 'badge-success';
    case 'POST': return 'badge-primary';
    case 'PUT': return 'badge-warning';
    case 'DELETE': return 'badge-danger';
    case 'PATCH': return 'badge-info';
    default: return 'badge-secondary';
  }
};



const EndpointModal = ({ endpoint, onClose, onEndpointAdded, onEndpointUpdated, onError }) => {
  const [formData, setFormData] = useState({
    name: endpoint?.name || '',
    description: endpoint?.description || '',
    method: endpoint?.method || 'GET',
    url: endpoint?.url || '',
    body: endpoint?.body ? JSON.stringify(endpoint.body, null, 2) : '',
    urlParams: endpoint?.urlParams ? JSON.stringify(endpoint.urlParams, null, 2) : '',
    requiresAuth: endpoint?.requiresAuth || false,
    endpointType: endpoint?.endpointType || 'Custom',
    isActive: endpoint?.isActive !== undefined ? endpoint.isActive : true
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);

  const methods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

  const endpointTypes = [
    { value: 'SCollect', label: 'SCollect', description: 'ServiceNow Service Catalog Collection API' },
    { value: 'Solutions By Text', label: 'Solutions By Text', description: 'Solutions By Text API integration' },
    { value: 'Custom', label: 'Custom', description: 'Custom API endpoint' }
  ];

  const handleTestEndpoint = async () => {
    console.log('🔍 DEBUG: handleTestEndpoint called');
    setTesting(true);
    setTestResult(null);
    setTestError(null);

    try {
      // Validate required fields
      if (!formData.url) {
        throw new Error('URL is required for testing');
      }

      let body = null;
      let urlParams = null;

      console.log('🔍 DEBUG: formData.method:', formData.method);
      console.log('🔍 DEBUG: formData.urlParams:', formData.urlParams);

      // Parse body JSON if provided (for POST/PUT/PATCH requests)
      if (formData.body.trim() && ['POST', 'PUT', 'PATCH'].includes(formData.method)) {
        try {
          body = JSON.parse(formData.body);
          console.log('🔍 DEBUG: Parsed body:', body);
        } catch (error) {
          console.error('🔍 DEBUG: Body parse error:', error);
          throw new Error('Invalid JSON in body field');
        }
      }

      // Parse URL parameters JSON if provided (for GET requests)
      if (formData.urlParams.trim() && formData.method === 'GET') {
        try {
          urlParams = JSON.parse(formData.urlParams);
          console.log('🔍 DEBUG: Parsed urlParams:', urlParams);
        } catch (error) {
          console.error('🔍 DEBUG: URL params parse error:', error);
          throw new Error('Invalid JSON in URL parameters field');
        }
      }

      const testEndpoint = {
        ...formData,
        body,
        urlParams
      };

      console.log('🔍 DEBUG: testEndpoint object:', testEndpoint);
      console.log('🔍 DEBUG: About to call adminAPI.testEndpoint');

      const response = await adminAPI.testEndpoint(testEndpoint);
      console.log('🔍 DEBUG: Response received:', response);
      setTestResult(response.data);
    } catch (error) {
      console.error('🔍 DEBUG: Error caught:', error);
      console.error('🔍 DEBUG: Error message:', error.message);
      console.error('🔍 DEBUG: Error response:', error.response);
      setTestError(error.response?.data?.error || error.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let body = null;
      let urlParams = null;

      // Parse body JSON if provided (for POST/PUT/PATCH requests)
      if (formData.body.trim() && ['POST', 'PUT', 'PATCH'].includes(formData.method)) {
        try {
          body = JSON.parse(formData.body);
        } catch (error) {
          throw new Error('Invalid JSON in body field');
        }
      }

      // Parse URL parameters JSON if provided (for GET requests)
      if (formData.urlParams.trim() && formData.method === 'GET') {
        try {
          urlParams = JSON.parse(formData.urlParams);
        } catch (error) {
          throw new Error('Invalid JSON in URL parameters field');
        }
      }

      const payload = {
        ...formData,
        body,
        urlParams
      };

      if (endpoint) {
        const response = await adminAPI.updateEndpoint(endpoint.id, payload);
        onEndpointUpdated(response.data.endpoint || { ...endpoint, ...payload });
      } else {
        const response = await adminAPI.createEndpoint(payload);
        onEndpointAdded(response.data.endpoint || { id: Date.now(), ...payload });
      }
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Failed to save endpoint');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h3>{endpoint ? 'Edit API Endpoint' : 'Add New API Endpoint'}</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="form-input"
              required
              placeholder="e.g., Sales API"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="form-input"
              rows="2"
              placeholder="Brief description of this endpoint"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Endpoint Type *</label>
            <select
              value={formData.endpointType}
              onChange={(e) => setFormData({...formData, endpointType: e.target.value})}
              className="form-input"
              required
            >
              {endpointTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
              {endpointTypes.find(type => type.value === formData.endpointType)?.description}
            </small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '15px' }}>
            <div className="form-group">
              <label className="form-label">Method *</label>
              <select
                value={formData.method}
                onChange={(e) => setFormData({...formData, method: e.target.value})}
                className="form-input"
                required
              >
                {methods.map(method => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">URL *</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                className="form-input"
                required
                placeholder="https://api.example.com/data"
              />
            </div>
          </div>

          {formData.method === 'GET' && (
            <div className="form-group">
              <label className="form-label">URL Parameters (JSON)</label>
              <textarea
                value={formData.urlParams}
                onChange={(e) => setFormData({...formData, urlParams: e.target.value})}
                className="form-input"
                rows="6"
                placeholder='{"startDate": "{{monthStartDate}}", "endDate": "{{monthEndDate}}", "limit": 100}'
              />
              <div style={{ marginTop: '8px' }}>
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  URL parameters for GET requests. These will be appended as query parameters (?key=value&key2=value2). You can use dynamic variables:
                </small>
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '4px', 
                  padding: '10px',
                  fontSize: '11px',
                  fontFamily: 'monospace'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px' }}>
                    <div><code>{'{{currentMonth}}'}</code> - Current month ("08")</div>
                    <div><code>{'{{currentMonthNum}}'}</code> - Current month (8)</div>
                    <div><code>{'{{currentYear}}'}</code> - Current year ("2025")</div>
                    <div><code>{'{{currentYearNum}}'}</code> - Current year (2025)</div>
                    <div><code>{'{{monthStartDate}}'}</code> - Month start (YYYY-MM-DD)</div>
                    <div><code>{'{{monthEndDate}}'}</code> - Month end (YYYY-MM-DD)</div>
                    <div><code>{'{{currentMonthName}}'}</code> - Month name (August)</div>
                    <div><code>{'{{currentMonthShort}}'}</code> - Month short (Aug)</div>
                    <div><code>{'{{currentTimestamp}}'}</code> - Unix timestamp</div>
                    <div><code>{'{{currentISODate}}'}</code> - ISO date string</div>
                    <div><code>{'{{custom.variableName}}'}</code> - User custom variables</div>
                    <div style={{ gridColumn: 'span 2', fontSize: '10px', color: '#666', marginTop: '4px' }}>
                      Custom variables are defined by users in their Settings → Endpoint Configurations
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {['POST', 'PUT', 'PATCH'].includes(formData.method) && (
            <div className="form-group">
              <label className="form-label">Request Body (JSON)</label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData({...formData, body: e.target.value})}
                className="form-input"
                rows="6"
                placeholder='{"key": "value", "month": "{{currentMonth}}", "year": "{{currentYear}}"}'
              />
              <div style={{ marginTop: '8px' }}>
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                  JSON body for {formData.method} requests. You can use dynamic variables:
                </small>
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '4px', 
                  padding: '10px',
                  fontSize: '11px',
                  fontFamily: 'monospace'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px' }}>
                    <div><code>{'{{currentMonth}}'}</code> - Current month ("08")</div>
                    <div><code>{'{{currentMonthNum}}'}</code> - Current month (8)</div>
                    <div><code>{'{{currentYear}}'}</code> - Current year ("2025")</div>
                    <div><code>{'{{currentYearNum}}'}</code> - Current year (2025)</div>
                    <div><code>{'{{monthStartDate}}'}</code> - Month start (YYYY-MM-DD)</div>
                    <div><code>{'{{monthEndDate}}'}</code> - Month end (YYYY-MM-DD)</div>
                    <div><code>{'{{currentMonthName}}'}</code> - Month name (August)</div>
                    <div><code>{'{{currentMonthShort}}'}</code> - Month short (Aug)</div>
                    <div><code>{'{{currentTimestamp}}'}</code> - Unix timestamp</div>
                    <div><code>{'{{currentISODate}}'}</code> - ISO date string</div>
                    <div><code>{'{{custom.variableName}}'}</code> - User custom variables</div>
                    <div style={{ gridColumn: 'span 2', fontSize: '10px', color: '#666', marginTop: '4px' }}>
                      Custom variables are defined by users in their Settings → Endpoint Configurations
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={formData.requiresAuth}
                onChange={(e) => setFormData({...formData, requiresAuth: e.target.checked})}
                style={{ marginRight: '8px' }}
              />
              Requires Authentication
            </label>
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
              Authentication will be handled using user-specific endpoint configurations from their Settings page.
            </small>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                style={{ marginRight: '8px' }}
              />
              Active
            </label>
          </div>

          {/* Test Endpoint Section */}
          <div className="form-group" style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h4 style={{ margin: 0, color: '#333' }}>Test Endpoint</h4>
              <button
                type="button"
                onClick={handleTestEndpoint}
                disabled={testing || !formData.url}
                className="btn btn-info"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <FiPlay style={{ marginRight: '5px' }} />
                {testing ? 'Testing...' : 'Test Endpoint'}
              </button>
            </div>

            {testResult && (
              <div style={{ 
                backgroundColor: '#d4edda', 
                border: '1px solid #c3e6cb', 
                borderRadius: '5px', 
                padding: '15px',
                marginBottom: '15px'
              }}>
                <h5 style={{ color: '#155724', margin: '0 0 10px 0' }}>✅ Test Successful</h5>
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '3px', 
                  padding: '10px',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  <pre style={{ 
                    margin: 0, 
                    fontSize: '12px', 
                    color: '#495057',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {testError && (
              <div style={{ 
                backgroundColor: '#f8d7da', 
                border: '1px solid #f5c6cb', 
                borderRadius: '5px', 
                padding: '15px',
                marginBottom: '15px'
              }}>
                <h5 style={{ color: '#721c24', margin: '0 0 10px 0' }}>❌ Test Failed</h5>
                <p style={{ color: '#721c24', margin: 0 }}>{testError}</p>
              </div>
            )}

            {!testResult && !testError && !testing && (
              <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                Test your endpoint configuration before saving to ensure it works correctly.
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Saving...' : (endpoint ? 'Update Endpoint' : 'Create Endpoint')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EndpointManagement;
